/*───────────────────────────────────────────────────────────────
 📄 File: components/ui/DataGridTable.tsx
 🧩 Description:
    Sortable <table> for DataGridSimple: thead with sort icons and
    tbody with alternating row stripes, column renderers and actions.
    Extracted from DataGridSimple.tsx.
────────────────────────────────────────────────────────────────*/

import { ChevronUp, ChevronDown, ArrowUpDown } from 'lucide-react';
import PaginationAdvanced from './PaginationAdvanced';

interface Column {
    key: string; label: string; visible: boolean; sortable?: boolean;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    render?: (value: any, item: any) => React.ReactNode;
    width?: string; minWidth?: string; maxWidth?: string;
    align?: 'left' | 'center' | 'right';
}

interface Pagination { page: number; limit: number; total: number; totalPages: number; }

interface Props {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    data: any[];
    loading: boolean;
    rowKey: string;
    visibleColumns: Column[];
    sortBy: string;
    sortOrder: 'asc' | 'desc';
    onSort: (col: string) => void;
    pagination: Pagination;
    onPageChange: (page: number) => void;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    renderActions?: (item: any) => React.ReactNode;
    translations?: { showing?: string; to?: string; of?: string; results?: string; previous?: string; next?: string; page?: string; };
}

export function DataGridTable({ data, loading, rowKey, visibleColumns, sortBy, sortOrder, onSort, pagination, onPageChange, renderActions, translations }: Props) {
    const alignCls = (a?: string) => a === 'right' ? 'text-right' : a === 'center' ? 'text-center' : 'text-left';
    const justifyCls = (a?: string) => a === 'right' ? 'justify-end' : a === 'center' ? 'justify-center' : 'justify-start';
    const colStyle = (c: Column) => ({ width: c.width, minWidth: c.minWidth, maxWidth: c.maxWidth });

    return (
        <div className={`transition-opacity duration-75 ${loading ? 'opacity-60' : 'opacity-100'}`}>
            <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-border">
                    <thead className="bg-muted/50">
                        <tr>
                            {visibleColumns.map((col) => (
                                <th key={col.key} className={`px-4 py-2 text-xs font-medium text-muted-foreground uppercase tracking-wider cursor-pointer hover:bg-muted/70 transition-colors ${alignCls(col.key === 'actions' ? 'right' : col.align)}`} onClick={() => col.sortable !== false && onSort(col.key)} style={col.key === 'actions' ? { width: col.width || '140px', minWidth: col.minWidth, maxWidth: col.maxWidth } : colStyle(col)}>
                                    <div className={`flex items-center gap-2 ${justifyCls(col.key === 'actions' ? 'right' : col.align)}`}>
                                        {col.sortable !== false && (
                                            <div className="flex flex-col items-center">
                                                {sortBy === col.key
                                                    ? sortOrder === 'asc' ? <ChevronUp className="h-3 w-3 text-primary" /> : <ChevronDown className="h-3 w-3 text-primary" />
                                                    : <ArrowUpDown className="h-3 w-3 text-muted-foreground/50" />}
                                            </div>
                                        )}
                                        <span className="text-xs font-medium text-muted-foreground uppercase tracking-wider">{col.label}</span>
                                    </div>
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-border">
                        {(data || []).map((item, idx) => (
                            <tr key={item[rowKey] || `row-${idx}`} className={`transition-colors ${idx % 2 === 0 ? 'bg-card hover:bg-muted/50' : 'bg-muted/30 hover:bg-muted/60'}`}>
                                {visibleColumns.map((col) => (
                                    <td key={col.key} className={`px-4 py-2 text-sm ${alignCls(col.key === 'actions' ? 'right' : col.align)}`} style={col.key === 'actions' ? { width: col.width || '140px', minWidth: col.minWidth, maxWidth: col.maxWidth } : colStyle(col)}>
                                        {col.key === 'actions' && renderActions
                                            ? <div className="flex justify-end w-full">{renderActions(item)}</div>
                                            : col.render
                                                ? col.render(item[col.key], item)
                                                : <div className="text-foreground">{item[col.key] || '-'}</div>}
                                    </td>
                                ))}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {pagination.totalPages > 1 && (
                <PaginationAdvanced
                    currentPage={pagination.page} totalPages={pagination.totalPages}
                    totalItems={pagination.total} itemsPerPage={pagination.limit}
                    onPageChange={onPageChange} translations={translations}
                />
            )}
        </div>
    );
}
